import { action, computed, observable } from 'mobx';
import copy from '@src/components/copy';
import Toast from '@src/components/toast';
import $$tracker from '@src/tracker';
import onPageShowByCache from '@src/utils/on-page-show-by-cache';
import useMount from '@src/utils/hooks/use-mount';
import ActivityConfigStore from '@src/pages/shared/activity-config-store';
import Services from '@src/services/general';

const GIFT_SLOT_COUNT = 9;
const TEAM_ACTION_LABELS = {
  create_team: '创建购买队伍',
  join_team: '加入购买队伍',
  invite_friend: '复制组件链接',
  team_success: '组队购买成功',
  team_full: '队伍已满',
  team_unavailable: '暂不可用'
};
const createEmptyGiftSlot = giftId => ({
  giftId,
  name: '',
  price: 0,
  supplementalPrice: 0,
  purchaseCount: 0,
  isPurchased: false,
  isPlaceholder: true
});

class GiftStore extends ActivityConfigStore {
  @observable isOrdering = false;
  @observable teamInviteTinyUrl = '';
  @observable isHomeActivityEnded = false;
  @observable purchasePhase = 'closed';
  @observable giftSlots = Array.from({ length: GIFT_SLOT_COUNT }, (item, index) => createEmptyGiftSlot(index + 1));
  // gift_list.team 是组队礼包主页面操作的唯一状态源。
  @observable
  teamViewModel = {
    actionState: 'team_unavailable',
    actionLabel: TEAM_ACTION_LABELS.team_unavailable,
    teamId: '',
    memberCount: 0,
    canInteract: false
  };

  normalizeConfig(data) {
    const config = data || {};
    const purchasePhase = Number(config.buy_status) === 1 ? 'presale' : Number(config.v_exp_time_status) === 1 ? 'supplemental' : 'closed';
    return {
      ...super.normalizeConfig(config),
      purchasePhase,
      isPurchaseOpen: purchasePhase !== 'closed'
    };
  }

  @action.bound
  setConfig(viewModel) {
    super.setConfig(viewModel);
    this.purchasePhase = viewModel.purchasePhase;
  }

  @computed
  get giftViewModels() {
    return this.giftSlots.map(gift => {
      const isPurchaseOpen = this.purchasePhase !== 'closed';
      return {
        ...gift,
        displayPrice: this.purchasePhase === 'supplemental' ? gift.supplementalPrice : gift.price,
        pricePhase: this.purchasePhase,
        isPurchaseOpen,
        // 已购买结果态优先于活动时段，避免活动结束后丢失支付结果。
        actionState: gift.isPlaceholder ? 'unavailable' : gift.isPurchased ? 'purchased' : isPurchaseOpen ? 'available' : 'unavailable'
      };
    });
  }

  @computed
  get teamActionViewModel() {
    // 未登录用户尚无可信账号购买态，统一提供登录入口。
    if (!this.loginState && !this.account) {
      return {
        actionState: 'create_team',
        actionLabel: TEAM_ACTION_LABELS.create_team,
        teamId: '',
        memberCount: 0,
        canInteract: true
      };
    }
    const teamGift = this.giftSlots[0];
    if (!teamGift || teamGift.isPlaceholder || !teamGift.isPurchased) {
      return {
        actionState: 'create_team',
        actionLabel: TEAM_ACTION_LABELS.create_team,
        teamId: '',
        memberCount: 0,
        canInteract: true
      };
    }
    const team = this.teamViewModel;
    const isPendingTeam =
      team.memberCount < 2 &&
      (team.actionState === 'invite_friend' || (team.memberCount === 1 && !['team_full', 'team_success', 'team_unavailable'].includes(team.actionState)));
    const actionState = isPendingTeam ? 'invite_friend' : 'team_success';
    return {
      actionState,
      actionLabel: TEAM_ACTION_LABELS[actionState],
      teamId: team.teamId,
      memberCount: team.memberCount,
      canInteract: actionState === 'invite_friend'
    };
  }

  normalizeGiftList(list) {
    const giftsById = new Map();
    if (Array.isArray(list)) {
      list.forEach(item => {
        const giftId = Number(item && item.gift_id);
        if (giftId >= 1 && giftId <= GIFT_SLOT_COUNT && !giftsById.has(giftId)) {
          giftsById.set(giftId, item);
        }
      });
    }

    return Array.from({ length: GIFT_SLOT_COUNT }, (item, index) => {
      const giftId = index + 1;
      const gift = giftsById.get(giftId);
      if (!gift) {
        return createEmptyGiftSlot(giftId);
      }
      return {
        giftId,
        name: gift.name || '',
        price: Math.max(0, Number(gift.price) || 0),
        supplementalPrice: Math.max(0, Number(gift.v_exp_price) || 0),
        purchaseCount: Math.max(0, Number(gift.amount) || 0),
        isPurchased: Number(gift.status) === 1,
        isPlaceholder: false
      };
    });
  }

  /**
   * 将 gift_list.team 原始字段收敛为组队礼包按钮使用的稳定状态。
   * @param {Object} team gift_list 返回的当前账号组队状态
   * @returns {Object} 组队礼包按钮 ViewModel
   */
  normalizeTeam(team) {
    const source = team || {};
    const actionState = TEAM_ACTION_LABELS[source.button_status] ? source.button_status : 'team_unavailable';
    return {
      actionState,
      actionLabel: TEAM_ACTION_LABELS[actionState],
      teamId: source.team_id || '',
      memberCount: Math.max(0, Number(source.member_count) || 0),
      canInteract: ['create_team', 'join_team', 'invite_friend'].includes(actionState)
    };
  }

  @action.bound
  setGiftSlots(giftSlots) {
    this.giftSlots = giftSlots;
  }

  @action.bound
  setTeamViewModel(team) {
    this.teamViewModel = this.normalizeTeam(team);
  }

  @action.bound
  setHomeActivityEnded(isEnded) {
    this.isHomeActivityEnded = Boolean(isEnded);
  }

  async loadHomeActivityState() {
    try {
      await this.$ztService.showAccountList();
      this.setHomeActivityEnded(false);
    } catch (error) {
      const response = (error && error.serviceResponse) || error || {};
      this.setHomeActivityEnded(Number(response.code) === -10154);
    }
    return this.isHomeActivityEnded;
  }

  async loadGiftList() {
    try {
      const response = await this.$ztService.giftList();
      this.setGiftSlots(this.normalizeGiftList(response && response.gift_list));
      this.setTeamViewModel(response && response.team);
      this.prepareTeamInviteTinyUrl();
    } catch (error) {
      this.setGiftSlots(this.normalizeGiftList());
      this.setTeamViewModel();
    }
    return this.giftViewModels;
  }

  /** @returns {Promise<boolean>|boolean} 是否发起下单或成功复制邀请链接 */
  handleTeamGiftClick() {
    const { actionState } = this.teamActionViewModel;
    if (actionState === 'create_team') {
      $$tracker.clickCreateTeam();
    }
    if (actionState === 'team_success') {
      return false;
    }
    if (this.isHomeActivityEnded) {
      Toast.info('活动已结束', 1);
      return false;
    }
    if (!this.loginState && !this.account) {
      this.login();
      return false;
    }
    if (actionState === 'create_team') {
      return this.createGiftOrder(1, '');
    }
    if (actionState === 'invite_friend') {
      return this.copyTeamInvite();
    }
    return false;
  }

  async handleGiftClick(giftId) {
    const gift = this.giftViewModels.find(item => item.giftId === Number(giftId));
    $$tracker.clickBuyGoods((gift && gift.name) || '');
    if (!this.loginState && !this.account) {
      this.login();
      return false;
    }
    if (!gift || gift.isPlaceholder) {
      Toast.fail('礼包信息获取失败，请稍后重试', 1);
      return false;
    }
    if (gift.isPurchased) {
      Toast.info('该礼包已购买', 1);
      return false;
    }
    if (!gift.isPurchaseOpen) {
      Toast.info('活动已结束', 1);
      return false;
    }
    return this.createGiftOrder(gift.giftId);
  }

  /**
   * 创建移动端礼包订单并跳转支付，teamId 仅用于加入组队礼包。
   * @param {number} giftId gift_list 中的礼包编号
   * @param {string} teamId 邀请队伍 ID，队长创建及普通礼包留空
   * @returns {Promise<boolean>} 是否成功发起支付跳转
   */
  async createGiftOrder(giftId, teamId = '') {
    if (this.isOrdering) {
      return false;
    }
    this.isOrdering = true;
    Toast.loading('正在创建订单...', 0, null, false);
    try {
      const result = await this.$ztService.createOrder(giftId, 'mobile', window.location.href, teamId);
      Toast.hide();
      if (!result || !result.pay_url) {
        Toast.fail('支付地址获取失败，请稍后重试', 1);
        return false;
      }
      window.location.href = result.pay_url;
      return true;
    } catch (error) {
      Toast.hide();
      const response = (error && error.serviceResponse) || error || {};
      if (Number(giftId) === 1 && Number(response.code) === -20109) {
        this.setTeamViewModel({
          button_status: 'team_success',
          member_count: this.teamViewModel.memberCount,
          team_id: this.teamViewModel.teamId || teamId
        });
        return false;
      }
      Toast.fail(response.message || response.msg || '创建订单失败，请稍后重试', 1);
      return false;
    } finally {
      this.isOrdering = false;
    }
  }

  buildTeamInviteUrl(teamId = '') {
    if (!teamId || typeof window === 'undefined') {
      return '';
    }
    // 后端只返回 team_id，使用当前部署地址可自动适配测试与正式环境。
    const link = document.createElement('a');
    link.href = window.location.href;
    link.search = `?team_id=${encodeURIComponent(teamId)}`;
    link.hash = '/';
    return link.href;
  }

  async prepareTeamInviteTinyUrl() {
    if (this.teamViewModel.actionState !== 'invite_friend') {
      this.teamInviteTinyUrl = '';
      return '';
    }
    const inviteUrl = this.buildTeamInviteUrl(this.teamViewModel.teamId);
    if (!inviteUrl) {
      return '';
    }
    try {
      const response = await Services.createTinyUrl(inviteUrl);
      this.teamInviteTinyUrl = (response && response.tiny_url) || '';
    } catch (error) {
      this.teamInviteTinyUrl = '';
    }
    return this.teamInviteTinyUrl;
  }

  /** @returns {Promise<boolean>} 是否成功复制前端生成的邀请链接 */
  async copyTeamInvite() {
    $$tracker.clickUrlBuy();
    const inviteUrl = this.buildTeamInviteUrl(this.teamViewModel.teamId);
    if (!inviteUrl) {
      Toast.info('邀请链接暂不可用', 1);
      return false;
    }
    const shareText = `我已预约《剑侠世界》首个生态服，快和我组队购买钜惠礼包，链接：${this.teamInviteTinyUrl || inviteUrl}，一起玩新服这波福利不能错过！`;
    copy(shareText, { autoDisplaySupportedAlert: false, autoDisplayUnsupportedAlert: false });
    return copy(shareText, {
      showText: false,
      successPreText: '邀请链接已复制',
      successToastDuration: 1000
    }).isSupport;
  }

  use() {
    super.use();
    useMount(() => {
      Promise.all([this.loadGiftList(), this.loadHomeActivityState()]);
      return onPageShowByCache(() => Promise.all([this.loadConfig(), this.loadGiftList(), this.loadHomeActivityState()]));
    });
  }
}

export default GiftStore;
